import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Shared email format check used by every form that collects an email.
// Mirrors the server-side rule (something@something.tld, no spaces).
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Mirrors the API's international person-name policy. */
export const PERSON_NAME_ERROR =
  "Name may only contain letters, spaces, hyphens, apostrophes, or periods";
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}]+(?:[ .'-]+[\p{L}\p{M}]+)*$/u;
export function normalizePersonName(value: string): string {
  return value.trim().replace(/\s+/gu, " ");
}

/** Removes characters that cannot be part of an international person name
 * while preserving separators so users can compose names during editing. */
export function sanitizePersonNameInput(value: string): string {
  return value.replace(/\s/gu, " ").replace(/[^\p{L}\p{M} .'-]/gu, "");
}
export function isValidPersonName(value: string): boolean {
  return PERSON_NAME_PATTERN.test(normalizePersonName(value));
}

/** Free text must contain at least one letter or number, not only whitespace or symbols. */
export function isMeaningfulText(value: string): boolean {
  return /[\p{L}\p{N}]/u.test(value);
}
export function normalizeOptionalText(value: string): string | null {
  return value.trim() || null;
}
export const MEANINGFUL_TEXT_ERROR =
  "Notes must contain at least one letter or number";

// ---- Shared birth date/time validation ----
// Native date inputs happily accept 5-6 digit years unless min/max are set,
// so every birth-date <Input type="date"> should spread these props AND the
// submit handler should call isValidBirthDate.
// Local calendar day (NOT UTC — toISOString() would be off by a day for
// users east/west of UTC around midnight).
export function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export const BIRTH_DATE_MIN = "1900-01-01";
// Called at render time so the max never goes stale in a long-lived tab.
export function birthDateInputProps(): { min: string; max: string } {
  return { min: BIRTH_DATE_MIN, max: todayISO() };
}

// Calendar-valid YYYY-MM-DD between 1900-01-01 and today (no future, no
// 5-digit years, no Feb 30).
export function isValidBirthDate(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return false;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year < 1900 || month < 1 || month > 12) return false;
  if (day < 1 || day > new Date(year, month, 0).getDate()) return false;
  const dob = new Date(year, month - 1, day);
  return dob.getTime() <= Date.now();
}

// Strict 24-hour HH:mm (00-23 : 00-59).
// Optional phone: digits with optional leading +, spaces/dashes/parens
// allowed, 7–15 digits total. Mirrors the server's check.
export function isValidPhone(value: string): boolean {
  const digits = value.trim().replace(/[\s\-().]/g, "");
  return /^\+?\d{7,15}$/.test(digits);
}

export function isValidTimeHHmm(s: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s.trim());
}

export function isValidTime24HourWithOptionalSeconds(s: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(s.trim());
}

export function stripMarkdown(text: string): string {
  if (!text) return text;
  return text
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^---+$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
}
